import { useMemo } from "react";
import { Box, Button, Chip, Stack, Typography } from "@mui/material";
import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import { labelSx, sectionTitleSx, valueSx } from "./styles";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const clean = (value: any) => {
    if (value === null || value === undefined) return "";
    const trimmed = String(value).trim();
    if (!trimmed) return "";
    const lower = trimmed.toLowerCase();
    if (lower === "null" || lower === "undefined" || lower === "n/a" || lower === "na") return "";
    return trimmed;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const isValidHttpUrl = (value: any) => {
    const v = clean(value);
    if (!v) return false;
    return /^https?:\/\//i.test(v);
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const isDataUri = (value: any) => {
    const v = clean(value);
    return v.startsWith("data:");
};

const looksLikePdf = (contentType: string, data: string) => {
    const ct = (contentType || "").toLowerCase();
    if (ct.includes("pdf")) return true;
    if (!data) return false;
    // Base64 PDF headers typically begin with "JVBER" (which is "%PDF" in base64).
    return typeof data === "string" && data.startsWith("JVBER");
};

const looksLikeImage = (contentType: string) => {
    const ct = (contentType || "").toLowerCase();
    return ct.startsWith("image/");
};

const iconFor = (contentType: string, data: string) => {
    if (looksLikePdf(contentType, data)) return PictureAsPdfOutlinedIcon;
    if (looksLikeImage(contentType)) return ImageOutlinedIcon;
    if ((contentType || "").toLowerCase().includes("text")) return DescriptionOutlinedIcon;
    return InsertDriveFileOutlinedIcon;
};

const humanContentType = (ct: string) => {
    const v = clean(ct);
    if (!v) return "File";
    if (/pdf/i.test(v)) return "PDF Document";
    if (/png/i.test(v)) return "PNG Image";
    if (/jpe?g/i.test(v)) return "JPEG Image";
    if (/html/i.test(v)) return "HTML Document";
    if (/text\/plain/i.test(v)) return "Text File";
    return v;
};

// Accepts either a `documentReference` object or a `binary` object (or both)
// and renders a proper attachment card. Hooks into the parent's PDF preview
// modal via `onPreviewBase64`.
const DocumentReferenceSection = ({
    documentReference,
    binary,
    onPreviewBase64
}: {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    documentReference?: any;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    binary?: any;
    onPreviewBase64?: () => void;
}) => {
    const contentType =
        clean(documentReference?.contentType) || clean(binary?.contentType) || "";
    const status = clean(documentReference?.status);
    const documentUrl = clean(documentReference?.documentUrl);
    const binaryData = clean(binary?.data);

    // Figure out what we can do with this attachment
    const canOpenExternal = isValidHttpUrl(documentUrl);
    const canPreviewBase64 = !!binaryData && !isValidHttpUrl(binaryData);
    const isPdf = looksLikePdf(contentType, binaryData);
    const Icon = iconFor(contentType, binaryData);
    const typeLabel = humanContentType(contentType);

    const base64DownloadUrl = useMemo(() => {
        if (!canPreviewBase64) return null;
        try {
            // Prefer an explicit data-URI if the backend already sent one
            if (isDataUri(binaryData)) return binaryData;
            return `data:${contentType || "application/octet-stream"};base64,${binaryData}`;
        } catch {
            return null;
        }
    }, [canPreviewBase64, binaryData, contentType]);

    const handleOpenExternal = () => {
        if (!canOpenExternal) return;
        window.open(documentUrl, "_blank", "noopener,noreferrer");
    };

    const handleDownload = () => {
        if (base64DownloadUrl) {
            const link = document.createElement("a");
            link.href = base64DownloadUrl;
            link.download = `document_${Date.now()}.${isPdf ? "pdf" : "bin"}`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            return;
        }
        if (canOpenExternal) {
            window.open(documentUrl, "_blank", "noopener,noreferrer");
        }
    };

    // Nothing actionable AND nothing to describe → let parent render the
    // "No attachments available" fallback instead.
    if (!contentType && !status && !documentUrl && !binaryData) return null;

    return (
        <Box
            sx={{
                p: 3,
                borderRadius: 3,
                border: "2px solid #f0f0f0",
                bgcolor: "#ffffff",
                boxShadow: "0 4px 16px rgba(0,0,0,0.05)",
                position: "relative",
                "&::before": {
                    content: '""',
                    position: "absolute",
                    left: 0,
                    top: 0,
                    bottom: 0,
                    width: "5px",
                    background: "linear-gradient(180deg, #2fbf71 0%, #27a864 100%)",
                    borderRadius: "3px 0 0 3px"
                }
            }}
        >
            <Typography sx={{ ...sectionTitleSx, ml: 1, mb: 2 }}>Attachment</Typography>
            <Stack direction={{ xs: "column", md: "row" }} spacing={2.5} alignItems={{ xs: "flex-start", md: "center" }} sx={{ ml: 1 }}>
                {/* File icon tile */}
                <Box
                    sx={{
                        width: 68,
                        height: 68,
                        borderRadius: 2.5,
                        bgcolor: "#d3f0e9",
                        color: "#2d6a4f",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                        border: "1.5px solid #b9e3cd"
                    }}
                >
                    <Icon sx={{ fontSize: 38 }} />
                </Box>

                {/* Meta */}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                        sx={{
                            fontWeight: 800,
                            fontSize: "1.05rem",
                            color: "#1f3a2e",
                            mb: 0.5,
                            wordBreak: "break-word"
                        }}
                    >
                        {typeLabel}
                    </Typography>
                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
                        {status ? (
                            <Chip
                                size="small"
                                label={status}
                                sx={{
                                    bgcolor: "#d3f0e9",
                                    color: "#2d6a4f",
                                    fontWeight: 700,
                                    letterSpacing: 0.4,
                                    textTransform: "uppercase",
                                    border: "1px solid #b9e3cd"
                                }}
                            />
                        ) : null}
                        {contentType ? (
                            <Chip
                                size="small"
                                label={contentType}
                                sx={{
                                    bgcolor: "#f1f3f6",
                                    color: "#5b6778",
                                    fontFamily: "monospace",
                                    fontWeight: 700,
                                    letterSpacing: 0.3,
                                    border: "1px solid #e2e6ec"
                                }}
                            />
                        ) : null}
                    </Stack>
                    {documentUrl && !canOpenExternal ? (
                        <Box>
                            <Typography sx={labelSx}>URL</Typography>
                            <Typography
                                sx={{
                                    ...valueSx,
                                    fontFamily: "monospace",
                                    fontSize: 12,
                                    wordBreak: "break-all"
                                }}
                            >
                                {documentUrl}
                            </Typography>
                        </Box>
                    ) : null}
                    {!canOpenExternal && !canPreviewBase64 ? (
                        <Typography sx={{ ...labelSx, fontStyle: "italic", mt: 0.5 }}>
                            The HIP did not return a downloadable copy for this attachment.
                        </Typography>
                    ) : null}
                </Box>

                {/* Actions */}
                <Stack
                    direction={{ xs: "row", md: "column" }}
                    spacing={1}
                    sx={{ flexShrink: 0, width: { xs: "100%", md: "auto" } }}
                >
                    {canPreviewBase64 && isPdf ? (
                        <Button
                            variant="contained"
                            startIcon={<VisibilityOutlinedIcon />}
                            onClick={() => onPreviewBase64?.()}
                            sx={{
                                bgcolor: "#2fbf71",
                                color: "#ffffff",
                                fontWeight: 700,
                                textTransform: "none",
                                px: 2,
                                "&:hover": { bgcolor: "#27a864" }
                            }}
                        >
                            Preview PDF
                        </Button>
                    ) : null}
                    {canOpenExternal ? (
                        <Button
                            variant={canPreviewBase64 && isPdf ? "outlined" : "contained"}
                            startIcon={<OpenInNewIcon />}
                            onClick={handleOpenExternal}
                            sx={{
                                fontWeight: 700,
                                textTransform: "none",
                                px: 2,
                                ...(canPreviewBase64 && isPdf
                                    ? { borderColor: "#2fbf71", color: "#2fbf71", "&:hover": { borderColor: "#27a864", bgcolor: "#f1fbf6" } }
                                    : { bgcolor: "#2fbf71", color: "#ffffff", "&:hover": { bgcolor: "#27a864" } })
                            }}
                        >
                            Open document
                        </Button>
                    ) : null}
                    {canPreviewBase64 ? (
                        <Button
                            variant="outlined"
                            startIcon={<DownloadOutlinedIcon />}
                            onClick={handleDownload}
                            sx={{
                                fontWeight: 700,
                                textTransform: "none",
                                px: 2,
                                borderColor: "#2fbf71",
                                color: "#2fbf71",
                                "&:hover": { borderColor: "#27a864", bgcolor: "#f1fbf6" }
                            }}
                        >
                            Download
                        </Button>
                    ) : null}
                </Stack>
            </Stack>
        </Box>
    );
};

export default DocumentReferenceSection;
